import { ChevronDownIcon } from '@langchain/untitled-ui-icons';

import { useEffect, useMemo, useState } from 'react';

import { Text } from '@/design-system';
import { useRunContents } from '@/hooks/useSwr';
import { RunSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { RunInputsAndOutputs } from '../Run/components/RunInputsAndOutputs';

export function ConversationTurnV2({
  run,
  idx,
  plainOptions,
  rootRuns,
  selectedRunId,
}: {
  run: RunSchema;
  idx: number;
  plainOptions: {
    input:
      | { type: 'key-heuristic' }
      | { type: 'key'; value: string }
      | { type: 'key'; value: string[] };
    output:
      | { type: 'key-heuristic' }
      | { type: 'key'; value: string }
      | { type: 'key'; value: string[] };
  };
  rootRuns: RunSchema[];
  selectedRunId: string | null;
}) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    if (selectedRunId === run.id) {
      setIsCollapsed(false);
    }
  }, [run.id, selectedRunId]);

  const { contents } = useRunContents({
    run: { data: run, isLoading: false },
    withContents: true,
  });

  const inputs = contents?.inputs.data ?? run.inputs;

  const outputs = contents?.outputs.data ?? run.outputs;
  const error = contents?.error.data ?? run.error;
  const updatedRun = useMemo(() => {
    return {
      ...run,
      inputs,
      outputs,
      error,
    };
  }, [error, inputs, outputs, run]);

  const inputsLoading = contents?.inputs.loading;
  const outputsLoading = contents?.outputs.loading;
  const errorLoading = contents?.error.loading;

  const runInputsAndOutputs = (
    <RunInputsAndOutputs
      variant={['plain', plainOptions]}
      run={updatedRun}
      prevRun={rootRuns.at(idx - 1)}
      inputsLoading={inputsLoading}
      outputsLoading={outputsLoading}
      errorLoading={errorLoading}
    />
  );

  return (
    <div
      key={run.id}
      className={cn(
        'flex flex-col',
        idx % 2 === 1 && 'bg-secondary',
        selectedRunId === run.id && 'bg-brand-tertiary'
      )}
    >
      <button
        type="button"
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="text-md flex w-full items-center justify-center gap-2 p-3 text-tertiary transition-all hover:bg-secondary hover:text-primary"
      >
        <Text variant="h4">Turn {idx + 1}</Text>
        <ChevronDownIcon
          className={cn(
            'h-4 w-4 transition-transform duration-200 ease-in-out',
            isCollapsed && 'rotate-[-90deg]'
          )}
        />
      </button>
      <div
        className={cn(
          'flex flex-col gap-4 overflow-hidden p-4 transition-all duration-300 ease-in-out',
          isCollapsed ? 'max-h-0 py-0 opacity-0' : 'max-h-[2000px] opacity-100'
        )}
      >
        {runInputsAndOutputs}
      </div>
    </div>
  );
}
